import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"
import { Icon } from "~/react/ui/icon"
import { Checkbox } from "~/react/ui/checkbox"
import { Radio } from "~/react/ui/radio"
import { Label } from "~/react/ui/label"

export interface OptionItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  id: string
  name: string
  value: string | number
  label: string
  description?: string
  multiple?: boolean
  icon?: string
  hint?: string
  logo?: string
  logoFallback?: string
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

const OptionItem = React.forwardRef<HTMLDivElement, OptionItemProps>(
  (
    {
      id,
      name,
      value,
      label,
      description,
      multiple = false,
      icon,
      hint,
      logo,
      logoFallback,
      checked,
      defaultChecked,
      disabled,
      onChange,
      className,
      inputProps: inputPropsOverride,
      ...props
    },
    ref,
  ) => {
    if (logo && !logoFallback) {
      throw new Error("logoFallback must be provided when logo is present")
    }

    const handleImageError = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (logoFallback) {
          event.currentTarget.src = logoFallback
        }
      },
      [logoFallback],
    )

    const inputProps = {
      id,
      name,
      value,
      checked,
      defaultChecked,
      disabled,
      onChange,
      ...inputPropsOverride,
    }

    return (
      <div
        ref={ref}
        className={cn("option-item", className)}
        {...props}
      >
        {icon && (
          <div className="option-item__icon">
            <Icon icon={icon} />
          </div>
        )}
        {logo && (
          <img
            className="option-item__logo"
            src={logo}
            alt=""
            onError={handleImageError}
          />
        )}
        <div className="option-item__body">
          <Label htmlFor={id} className="option-item__title">
            {label}
            {hint && (
              <span className="fw-normal text-xs text-hint">{hint}</span>
            )}
          </Label>
          {description && (
            <div className="option-item__description">{description}</div>
          )}
        </div>
        {multiple ? (
          <Checkbox className="option-item__input" {...inputProps} />
        ) : (
          <Radio className="option-item__input" {...inputProps} />
        )}
      </div>
    )
  },
)

OptionItem.displayName = "OptionItem"

const optionsGroupVariants = cva("options-group", {
  variants: {
    orientation: {
      vertical: "",
      horizontal: "options-group--horizontal",
    },
  },
  defaultVariants: {
    orientation: "vertical",
  },
})

export interface OptionsGroupProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof optionsGroupVariants> {}

const OptionsGroup = React.forwardRef<HTMLDivElement, OptionsGroupProps>(
  ({ className, orientation, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(optionsGroupVariants({ orientation }), className)}
        {...props}
      >
        {children}
      </div>
    )
  },
)

OptionsGroup.displayName = "OptionsGroup"

export { OptionItem, OptionsGroup }

